import { User } from 'app/core/user/user.types';
import { Partner } from 'app/modules/admin/partner-management/partner-management.types';
import { ReleaseSubscription } from 'app/modules/user/payments/payments.types';
import { Country, ImageFile } from 'app/shared/shared.types';

export type ReleaseStatus =
    'draft' |
    'pending-approval' |
    'pending-delivery' |
    'pending-release-date' |
    'rejected' |
    'rejected-permanently' |
    'subscription-expired' |
    'taken-down' |
    'published' |
    'deleted';

export enum AudioFormat {
    Master = "master",
    Mp3_96 = "mp3-96",
}

export type ReleaseHistoryStatus = ReleaseStatus | "imported";

export interface ReleaseHistoryEntry {
    createdAt: Date;
    status: ReleaseStatus;
    user: User;
    note?: string;
}

export interface ReleaseHistory {
    id: string;
    history: ReleaseHistoryEntry[];
}

export interface RejectionFormData {
    rejectionReason: string;
    permanentRejection: boolean;
}

export interface ApprovalStatusDTO {
    approvals: string[];
    rejectionReason?: string;
    permanentRejection?: boolean;
}

export interface TakeDownDTO {
    releases: string[];
    reason?: string;
    permanent?: boolean;
}

export class Release {
    uuid: string;
    catalogId?: string;
    userId: string;
    title: string;
    version: string;
    series: string;
    description: string;
    type: 'single' | 'album';
    label: Label;
    participants: ReleaseParticipant[];
    stores: Store[];
    publishAtFutureStores?: boolean;
    permanentlyRejected?: boolean;
    releaseDate?: Date;
    originalReleaseDate?: Date;
    preorderDate?: Date;
    ean?: string;
    primaryGenre: Genre;
    subGenre: Genre;
    language: Language;
    audioLanguage: Language | '0';
    rightsHolder?: string;
    copyrightText?: string;
    copyrightYear?: number;
    pText?: string;
    pYear?: number;
    artwork: ImageFile;
    revenue: number;
    format: string;
    status: ReleaseStatus;
    subscription?: ReleaseSubscription;
    tracks?: Track[];
    countryWhitelist: Country[];
    countryBlacklist: Country[];
    user?: User;
    approvals?: ReleaseApproval[];
    foreignEAN?: boolean;
    waitingForFingerprinting?: boolean;
}

export interface ReleaseApproval {
    uuid: string;
    release?: Release;
    releaseUuid?: string;
    action: string;
    status: string;
    data?: any;
    createdAt: Date;
    comment?: string;
}

export interface Artist {
    uuid?: string;
    createdAt?: Date;
    email?: string;
    releases?: Release[];
    featReleases?: Release[];
    trackParticipants?: TrackParticipant[];
    releaseParticipants?: ReleaseParticipant[];
    title: string;
    appleId?: string;
    spotifyId?: string;
    isni?: string;
    soundcloudId?: string;
    metaId?: string;
    amazonId?: string;
}

export interface ArtistSearchResult {
    title: string;
    spotifyId?: string;
    appleId?: string;
    imageUrl?: string;
}

export interface TrackParticipant {
    uuid?: string;
    sortOrder?: number;
    artist_uuid?: string;
    track_uuid?: string;
    participant?: Artist;
    track?: Track;
    email?: string;
    role: string;
    share: number | null;
}

export interface ReleaseParticipant {
    uuid?: string;
    sortOrder?: number;
    artist_uuid?: string;
    release_uuid?: string;
    participant?: Artist;
    release?: Release;
    email?: string;
    role: string;
    share?: number | null;
}

export enum ParticipantRole {
    MainArtist = "Main Artist",
    FeaturedArtist = 'Featured Artist',
    Vocalist = 'Vocalist',
    Producer = 'Producer',
    Remixer = 'Remixer',
    Mixer = 'Mixer',
    Composer = 'Composer',
    Conductor = 'Conductor',
    Soloist = 'Soloist',
    Performer = 'Performer',
    Arranger = 'Arranger',
    SongWriter = 'Song Writer',
    Engineer = 'Engineer',
    MasteringEngineer = 'Mastering Engineer',
    Choir = 'Choir',
    Orchestra = 'Orchestra',
    Ensemble = 'Ensemble',
    Lyricist = 'Lyricist',
    Publicist = 'Publicist',
    Other = 'Other',
}

export interface Label {
    uuid?: string;
    title: string;
    email?: string;
    releases?: Release[];
    user?: User;
    logo?: ImageFile;
    beatportPermission?: boolean;
}

export interface Language {
    alpha3: string;
}

export interface Genre {
    uuid: string;
    title: string;
}

export interface Store {
    uuid: string;
    id?: string;
    title: string;
    logo?: ImageFile;
    publishedAt?: Date;
    hasStreams: boolean;
    hasDownloads: boolean;
    partners?: Partner[];
}

export interface ReleaseStore {
    publishedAt: Date;
    release_uuid: string;
    store: Store;
    store_uuid: string;
}

export interface Track {
    uuid?: string;
    audioFile?: AudioFile;
    audioFiles: AudioFile[];
    title: string;
    version?: string;
    participants: TrackParticipant[];
    acrCloudResults: AcrCloudResult[];
    composer?: string;
    songwriter?: string;
    primaryGenre: Genre;
    subGenre: Genre;
    isrc?: string;
    iswc?: string;
    language?: Language | '0';
    explicit?: boolean;
    distributionType?: string;
    lyrics?: string;
    previewStart?: number;
    previewDuration?: number;
    copyrightText?: string;
    copyrightYear?: number;
    pText?: string;
    pYear?: number;
    foreignISRC?: boolean;
    releaseUuid?: string;
    release?: Release;
}

export interface AcrCloudResult {
    uuid?: string;
    isrc?: string;
    upc?: string;
    artistNames?: string;
    externalId?: string;
    labelName?: string;
    releaseTitle?: string;
    trackTitle?: string;
    score?: number;
    status: number;
    statusMessage?: string;
    sampleStartMs?: number;
    sampleDurationMs?: number;
    spotifyId?: string;
    appleMusicId?: string;
    youtubeId?: string;
    apiType: string;
}

export interface AudioFile {
    trackUuid?: string;
    type: AudioFormat;
    fileName: string | null;
    duration?: number | null;
    bitRate?: number | null;
    samplingRate?: number | null;
    bitDepth?: number | null;
    codec?: string | null;
    channels?: boolean | null;
    fileSize?: number | null;
    lossless?: boolean | null;
}
